<template>
  <section
      id="propertyRooms"
      itemscope
      itemtype="https://schema.org/Hotel"
      class="property-details property-details--rooms"
      :class="[{'block-split': (!store.rooms?.length && !loadingRooms)}, {'flex-col': soldOutPopup}, {'no-active': isDateChanged}]"
  >
    <!-- Google's price-accuracy validator reads Microdata, and the identifier
         must be the same hotel id we send in the price feed. -->
    <meta itemprop="name" :content="store.property?.name || ''">
    <meta itemprop="identifier" :content="String(store.property?.id || '')">
    <div
        v-if="microdataAddress"
        itemprop="address"
        itemscope
        itemtype="https://schema.org/PostalAddress">
      <meta itemprop="streetAddress" :content="microdataAddress.streetAddress">
      <meta itemprop="addressLocality" :content="microdataAddress.addressLocality">
      <meta itemprop="addressRegion" :content="microdataAddress.addressRegion">
      <meta itemprop="postalCode" :content="microdataAddress.postalCode">
      <meta itemprop="addressCountry" :content="microdataAddress.addressCountry">
    </div>

    <h2 class="text-title-2 fw-semi-bold pb-6 flex-colum">{{$t('Rooms')}}</h2>

    <!-- Free cancellation banner -->
    <div v-if="hasFreeCancel && !loadingRooms" class="free-cancel-banner">
      <img src="/icons/check-2.svg" alt="" width="16" height="16"/>
      <span>{{ $t('Free cancellation available on select rooms') }}</span>
    </div>

    <HotelRoomsSkeleton v-if="loadingRooms"/>

    <!-- No rooms available (dates set but empty result) -->
    <div v-if="!store.rooms?.length && !loadingRooms && hasDates && !soldOutPopup" class="rooms-error">
      <span class="icon icon-information-solid color-yellow"></span>
      <span>{{ $t('No rooms available for your selected dates.') }}</span>
      <button class="color-blue fw-medium" @click="openDatesSelector">{{ $t('Try different dates') }}</button>
    </div>

    <!-- Use ClientOnly for regular users (faster load), skip for Google bot (full SSR for SEO) -->
    <component :is="isGoogleBot ? 'div' : 'ClientOnly'">
      <template v-if="store.rooms?.length && !loadingRooms">
        <div
            v-for="(room, roomIndex) in store.rooms"
            :key="room.id"
            :id="room.id"
           itemprop="containsPlace"
           itemscope
           itemtype="https://schema.org/HotelRoom https://schema.org/Product"
           :class="['room', `room-${room.rates?.[0]?.ref || ''}`, { 'room--no-images': allRoomsLackImages }]">
        <meta itemprop="identifier" :content="String(room.id || '')">
        <div
            v-if="room.max_occupancy || room.rates?.[0]?.occupancy"
            itemprop="occupancy"
            itemscope
            itemtype="https://schema.org/QuantitativeValue">
          <meta itemprop="value" :content="String(room.max_occupancy || room.rates?.[0]?.occupancy)">
        </div>
        <div
            v-if="room.beds?.length"
            itemprop="bed"
            itemscope
            itemtype="https://schema.org/BedDetails">
          <meta itemprop="numberOfBeds" :content="String(room.beds.length)">
        </div>
        <div class="room-header" :class="{'room-header--compact': allRoomsLackImages}">
          <div class="room__name" itemprop="name">{{room.description}}</div>
          <div v-if="room.summary && allRoomsLackImages" class="room__summary text-body-2 color-dark-grey">{{room.summary}}</div>
        </div>
        <div class="room-details" :class="{'pt-3': !allRoomsLackImages, 'pt-2': allRoomsLackImages}">
          <div v-if="!allRoomsLackImages" class="room-details__left">
            <div class="room-images">
              <GalleryPreview v-if="room.images?.length" :images="room.images" url-field="url" @toggle-more-info="openRoomInfo(room)"/>
              <PhotoPlaceholder v-else class="image-placeholder" :font-size="'42px'"/>
              <!--<img v-else src="/images/image-placeholder.png" class="image-placeholder" alt="">-->
            </div>
            <div class="room-amenities">
              <span v-for="facility in facilityList(room)">
                <img
                    v-if="facility.iconType === 'image'"
                    class="room_amenity__icon"
                    width="18"
                    height="18"
                    :src="`/icons/icomoon/icons-svg/${facility.icon}.svg`"
                    alt="" aria-hidden="true">
                <span v-else class="icon" :class="facility.icon"/> {{facility.description}}
              </span>
            </div>
            <div class="pt-2" v-if="room?.amenities?.length">
              <button class="color-blue btn--more" @click="openRoomInfo(room)">
                {{$t('More details')}} <span class="icon icon-chevron-right"></span>
              </button>
            </div>
          </div>
          
          <!-- Alternative layout when all rooms lack images: show amenities inline -->
          <div v-if="allRoomsLackImages" class="room-details__amenities-compact">
            <div class="amenities-list">
              <span v-for="facility in facilityList(room)" :key="facility.description" class="amenity-item">
                <img
                    v-if="facility.iconType === 'image'"
                    class="room_amenity__icon"
                    width="14"
                    height="14"
                    :src="`/icons/icomoon/icons-svg/${facility.icon}.svg`"
                    alt="" aria-hidden="true">
                <span v-else class="icon" :class="facility.icon"/> 
                <span class="amenity-text">{{facility.description}}</span>
              </span>
            </div>
            <button v-if="room?.amenities?.length" class="color-blue btn--more-compact" @click="openRoomInfo(room)">
              {{$t('More details')}} <span class="icon icon-chevron-right"></span>
            </button>
          </div>
          
          <div class="room-details__right">
            <!-- v-show keeps unpriceable rates in the DOM; leaving them attached to
                 the Hotel would mark up an offer no guest can see. -->
            <label
                v-for="(rate, rateIndex) in room.rates"
                :for="rate.ref"
                :key="rate.ref"
                v-show="calculateRatePerNight(rate)"
                :itemprop="calculateRatePerNight(rate) ? 'offers' : null"
                itemscope
                itemtype="https://schema.org/Offer https://schema.org/LodgingReservation"
                class="room-rate"
                :class="{'room-rate__selected': selectedRate === rate.ref}"
            >
              <!-- No Offer.identifier: `rate.ref` is our booking token, not a rate-plan
                   id Google has ever seen, so it buys no feed-to-page matching and
                   costs ~60KB across the page. Binding the two needs the feed to emit
                   a real package id (the landing URL's google-package-id arrives empty). -->
              <template v-if="microdataCheckin && microdataCheckout">
                <meta itemprop="checkinTime" :content="microdataCheckin">
                <meta itemprop="checkoutTime" :content="microdataCheckout">
              </template>
              <div class="room-rate__radio">
                <RadioButton
                    v-model="selectedRate"
                    :value="rate.ref"
                    :inputId="rate.ref"
                    @change="() => selectedRateChange(rate)" />
              </div>
              <div class="room-rate_info">
                <label :for="rate.ref" class="flex">
                  <span class="flex-1 text-body-1 fw-medium">{{rate.description}}</span>
                </label>
                <div v-if="isGoogleMatchedRate(rate)" class="google-match-badge">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  {{ $t('As seen on Google') }}
                </div>
                <div class="room-rate_room_board" v-if="rate.room_board && rate.room_board.description">
                  <img src="/icons/room_board.svg" alt="room board">
                  <span>{{rate.room_board.description}}</span>
                </div>
                <div class="room-rate-description text-body-2">
                  <div class="cancellation-info refund-info fully-refundable" v-if="checkFreeCancellation(rate)">
                    <img src="/icons/check-2.svg" alt="refund">
                    <div class="label">
                      {{$t('Fully Refundable')}}
<!--                      <span>-->
<!--                      {{$t('until')}} {{ createCancellationDateInUserTimezone(rate) }} {{userTimezoneString()}}-->
<!--                    </span>-->
                    </div>
                    <span class="info-icon-wrapper" @mouseover="showCancellationInfo(roomIndex, rateIndex)" @mouseleave="hideCancellationInfo(roomIndex, rateIndex)">
                        <span class="icon icon-info"/>
                        <span class="icon icon-information-solid"></span>
                        <Tooltip v-show="cancellationInfoPanel[setIndex(roomIndex,rateIndex)]">
                          <div>
                            <span class="fw-bold">{{$t('Cancellation conditions')}}</span>
                            <br><br>
                            Before {{ createCancellationDateInUserTimezone(rate) }} {{userTimezoneString()}}: Free cancellation.
                            <br><br>
                            From {{ currentDate() }} to {{ createCancellationDateInUserTimezone(rate)}}  {{userTimezoneString()}} (Arrival date): 100% of the total amount of your stay will be charged to your credit card.
                            <br><br>
                            In case of no-show: 100% of the total amount of your stay will be charged to your credit card.
                          </div>
                        </Tooltip>
                      </span>
                  </div>

                  <div class="cancellation-info non-refundable" v-else>
                    <img src="/icons/check-2.svg" alt="refund">
                    <span class="label refund-info">
                      {{$t('Non Refundable')}}
                      <span class="info-icon-wrapper" @mouseover="showCancellationInfo(roomIndex, rateIndex)" @mouseleave="hideCancellationInfo(roomIndex, rateIndex)">
                        <span class="icon icon-info"/>
                        <span class="icon icon-information-solid"></span>
                        <Tooltip v-show="cancellationInfoPanel[setIndex(roomIndex,rateIndex)]">
                          <div>
                            <span class="fw-bold">{{$t('Cancellation conditions')}}</span>
                            <br><br>
                            This reservation is nonrefundable. Full payment is required at the time of booking, and no refunds will be issued for cancellations, modifications, or no-shows. Please ensure your travel plans are confirmed prior to booking.
                          </div>
                        </Tooltip>
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              <div class="room-details__price-col">
                <div class="price-header">
                  <div class="price-header__main">
                    <span v-if="rate.original_retail_price" class="price-header__strike">{{calculateOriginalRatePerNight(rate)}}</span>
                    <span class="price-header__amount">{{calculateRatePerNight(rate)}}</span>
                    <span v-if="rate.promotion_discount_percent" class="price-header__badge">-{{rate.promotion_discount_percent}}%</span>
                  </div>
                  <div class="price-header__sub">
                    <span>{{ $t('per night') }}</span>
                    <span v-if="rate.sell_mode === 'per_person'"> / {{$t('person')}}</span>
                  </div>
                  <div v-if="formatSavings(rate)" class="price-header__savings">{{ $t('You save') }} {{ formatSavings(rate) }}</div>
                  <div v-if="rate.promotion_name" class="price-header__promo">{{ rate.promotion_name }}</div>
                  <div v-if="googlePriceNotice && !isGoogleMatchedRate(rate) && rateIndex === 0 && roomIndex === 0" class="price-header__google-note">{{ googlePriceNotice }}</div>
                </div>
                <div
                    v-if="rate.total_price?.converted"
                    itemprop="priceSpecification"
                    itemscope
                    itemtype="https://schema.org/CompoundPriceSpecification">
                  <!-- Google reads `price` as the total INCLUDING taxes and fees. -->
                  <meta itemprop="priceCurrency" :content="rate.total_price.converted.currency || ''">
                  <meta itemprop="price" :content="microdataAmount(rate.total_price)">
                  <div
                      v-for="component in microdataPriceComponents(rate)"
                      :key="component.key"
                      itemprop="priceComponent"
                      itemscope
                      itemtype="https://schema.org/UnitPriceSpecification">
                    <meta itemprop="name" :content="component.name">
                    <meta itemprop="priceCurrency" :content="component.currency">
                    <meta itemprop="price" :content="component.amount">
                    <meta v-if="component.type" itemprop="priceComponentType" :content="component.type">
                  </div>
                </div>
                <div class="price-lines" v-if="rate.base_price?.converted">
                  <div v-if="bookingPeriod > 1" class="price-lines__row">
                    <span>{{ calculateRatePerNight(rate) }} x {{ bookingPeriod }} {{ $t('nights') }}</span>
                    <span>{{ formatPrice(rate.base_price) }}</span>
                  </div>
                  <div v-if="getTaxRecoveryFee(rate)" class="price-lines__row">
                    <span>{{ $t('Tax Recovery Fee') }}</span>
                    <span>{{ getTaxRecoveryFee(rate) }}</span>
                  </div>
                  <!-- getPayAtProperty sums MANDATORY surcharges, which are already inside
                       `total_price` printed directly below — so unqualified this row reads as an
                       amount to add to the Total. The microdata above states the containment, but
                       the visible text has to as well. Same wording as the booking block. -->
                  <div v-if="getPayAtProperty(rate)" class="price-lines__row price-lines__row--property">
                    <span>
                      {{ $t('Pay at property') }}
                      <span class="price-lines__note">{{ $t('included in the total below') }}</span>
                    </span>
                    <span>{{ getPayAtProperty(rate) }}</span>
                  </div>
                  <div class="price-lines__total">
                    <span>{{$t('Total')}}</span>
                    <span>{{ formatPrice(rate.total_price) }}</span>
                  </div>
                  <div class="price-lines__incl">{{ $t('includes taxes & fees') }}</div>
                </div>
                <div v-else-if="rate.total_price?.converted" class="price-lines">
                  <div class="price-lines__total">
                    <span>{{$t('Total')}}</span>
                    <span>{{ formatPrice(rate.total_price) }}</span>
                  </div>
                  <div class="price-lines__incl">{{ $t('includes taxes & fees') }}</div>
                </div>
              </div>
            </label>
          </div>
        </div>
      </div>
    </template>
    </component>

    <div v-if="!soldOutPopup && !store.rooms?.length && !loadingRooms" class="rooms-list-placeholder">
      <div class="text-body-1 placeholder-block rooms-list-placeholder__line">
        <span class="icon icon-information-solid color-yellow pr-2"></span>
        <div>
          <button class="color-blue fw-medium pr-1" @click="openDatesSelector">
            {{$t('Select dates')}}
          </button>
          <span>{{$t('to get availability and rates.')}}</span>
        </div>
      </div>
      <div class="rooms-list-placeholder__list" v-if="$siteConfig.brand !== 'Tripening'">
        <div class="placeholder-block">
          <img src="/images/image-placeholder.png" alt="" aria-hidden="true">
          <span>Room 1</span>
        </div>
        <div class="placeholder-block">
          <img src="/images/image-placeholder.png" alt="" aria-hidden="true">
          <span>Room 2</span>
        </div>
        <div class="placeholder-block">
          <img src="/images/image-placeholder.png" alt="" aria-hidden="true">
          <span>Room 3</span>
        </div>
        <div class="placeholder-block desktop-only">
          <img src="/images/image-placeholder.png" alt="" aria-hidden="true">
          <span>Room 4</span>
        </div>
        <div class="placeholder-block desktop-only">
          <img src="/images/image-placeholder.png" alt="" aria-hidden="true">
          <span>Room 5</span>
        </div>
      </div>
    </div>
    <div class="sold-out-rooms" v-show="soldOutPopup"></div>
    <DialogCloseOutside
        v-model="isModalVisible"
        :showHeader="false"
        position="right"
        :block-scroll="true"
        :modal="true"
        :draggable="false"
        class="room-modal-container custom-scrollbar"
    >

      <div class="pb-4 room-dialog-header-wrapper">
        <button class="mobile-only" @click="isModalVisible = false">
          <span class="icon icon-chevron-left color-blue text-title-1"></span>
        </button>
        <span class="flex-1 text-title-1 fw-bold room-info-dialog-header">{{$t('Room information')}}</span>
        <button class="desktop-only" @click="isModalVisible = false">
          <span class="icon icon-close color-blue text-title-1"></span>
        </button>
      </div>
      <template v-if="roomInfoDialogData">
        <PropertyPageRoomGallery :photos="roomInfoDialogData.images"/>
        <PropertyPageRoomDetails class="mt-6" :room="roomInfoDialogData"/>
      </template>
    </DialogCloseOutside>
    <Dialog
        v-model:visible="soldOutPopup"
        pt:mask:class="blur-bg"
        pt:root:class="info-popup"
        :show-header="false"
        append-to=".sold-out-rooms"
    >
      <div>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M11.3103 1.00902L10.7788 1.06406C8.19994 1.3214 5.60177 2.65073 3.84488 4.61307C1.9021 6.7832 0.91488 9.47626 1.00575 12.3455C1.1641 17.3009 4.57187 21.4966 9.40609 22.6911C10.8294 23.0346 12.3068 23.0923 13.7526 22.8608C15.1983 22.6293 16.5837 22.1132 17.8284 21.3425C20.2762 19.8182 22.0282 17.3952 22.7081 14.594C22.9449 13.6294 23 13.1381 23 11.9973C23 10.8565 22.9449 10.3652 22.7081 9.40058C21.6176 4.94884 17.9496 1.67368 13.3853 1.07645C12.9942 1.02553 11.5719 0.98012 11.3103 1.00902ZM13.1829 3.13925C14.7295 3.34273 16.1963 3.94556 17.4388 4.88829C17.8918 5.22681 18.7826 6.11716 19.1213 6.5699C19.8938 7.60337 20.4652 8.81848 20.724 9.9813C21.0187 11.3092 21.0187 12.6854 20.724 14.0133C20.154 16.5743 18.3489 18.8476 15.9559 20.0173C15.0932 20.4393 14.1703 20.7252 13.22 20.865C12.6362 20.9544 11.3805 20.9544 10.7967 20.865C8.11457 20.4577 5.85099 18.9949 4.42592 16.7463C4.07539 16.1654 3.783 15.5515 3.55298 14.9133C3.22502 13.9766 3.06424 12.9896 3.07796 11.9973C3.07796 10.5675 3.35471 9.34828 3.96742 8.08914C4.43556 7.12861 4.91196 6.46532 5.69265 5.68506C6.38316 4.96923 7.19824 4.38496 8.09805 3.96079C9.67462 3.19107 11.4439 2.90521 13.1829 3.13925ZM11.6036 6.89192C11.2952 6.99237 11.1203 7.16852 11.0349 7.463C10.9854 7.63089 10.9799 8.01483 10.9881 10.368C10.9991 13.0789 10.9991 13.0803 11.079 13.2303C11.1839 13.4224 11.3523 13.5723 11.5554 13.6542C11.7371 13.7175 12.2796 13.7175 12.4614 13.6542C12.6645 13.5723 12.8329 13.4224 12.9378 13.2303C13.0176 13.0803 13.0176 13.0789 13.0287 10.368C13.0397 7.40658 13.0397 7.40383 12.829 7.15475C12.6583 6.95109 12.4751 6.88366 12.0635 6.87265C11.9099 6.864 11.7559 6.87045 11.6036 6.89192ZM11.7137 15.4789C11.4383 15.5773 11.2091 15.7745 11.0707 16.0321C10.9713 16.2789 10.9592 16.5522 11.0363 16.8068C11.1108 17.0062 11.243 17.1788 11.4162 17.3026C11.5894 17.4264 11.7956 17.4958 12.0084 17.5018C12.4256 17.5018 12.851 17.199 12.9805 16.8068C13.0576 16.5522 13.0454 16.2789 12.946 16.0321C12.8321 15.8311 12.6667 15.6639 12.4669 15.5477C12.3072 15.4651 11.8762 15.4252 11.7137 15.4789Z" fill="red"/>
        </svg>


        <div class="info-popup__body">
          <h2>{{$t('No available rooms')}}</h2>
          <div>

            Sorry, all rooms are booked for the selected date. Please choose another date.

          </div>

          <button
              class="btn btn-primary mt-2"
              @click="closeInfoPopupAndOpenDates"
          >
            Select another date
          </button>
        </div>

      </div>

      <!-- loading -->
      <div v-if="false">
        <LazyLoader/>
        Please wait, payment processing is in progress
      </div>
    </Dialog>
    <Dialog
        v-if="currentSoldOut"
        v-model:visible="soldOutRatePopup"
        pt:mask:class="blur-bg"
        pt:root:class="info-popup"
        :show-header="false"
        :modal="true"
    >
      <div>
        <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M33.8896 26.4238C35.4624 23.3018 36.0105 19.7633 35.4558 16.3118C34.9012 12.8602 33.272 9.67163 30.8001 7.19973C28.3283 4.72782 25.1398 3.09858 21.6884 2.54389C18.2369 1.98919 14.6985 2.5373 11.5766 4.11021M7.29756 7.20608C5.73386 8.74397 4.49012 10.5762 3.63807 12.5972C2.78601 14.6182 2.34252 16.7879 2.33316 18.9811C2.3238 21.1743 2.74876 23.3477 3.58354 25.3759C4.41831 27.4041 5.64638 29.2469 7.1969 30.7981C8.74741 32.3492 10.5897 33.5781 12.6175 34.4137C14.6453 35.2493 16.8184 35.6751 19.0116 35.6666C21.2048 35.6581 23.3746 35.2154 25.3958 34.3642C27.4171 33.5129 29.2498 32.2698 30.7882 30.7067M2.42959 2.34167L35.6581 35.5711" stroke="#E20E0E" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>



        <div class="info-popup__body">
          <div>

            This room is not available for your dates

          </div>

          <button
              class="btn btn-primary mt-2"
              @click="closeInfoPopupAndOpenDates"
          >
            Search again
          </button>
        </div>

      </div>

      <!-- loading -->
      <div v-if="false">
        <LazyLoader/>
        Please wait, payment processing is in progress
      </div>
    </Dialog>
  </section>
</template>

<script setup lang="ts">
const { $siteConfig } = useNuxtApp()
import RadioButton from 'primevue/radiobutton';
import {usePropertyStore} from '~/store/property'
import DialogCloseOutside from '~/components/DialogCloseOutside.vue';
import PropertyPageRoomGallery from "~/components/PropertyPage/RoomDetails/PropertyPageRoomGallery.vue";
import PropertyPageRoomDetails from "~/components/PropertyPage/RoomDetails/PropertyPageRoomDetails.vue";
import PropertyReviewsHeader from "~/components/PropertyReviews/PropertyReviewsHeader.vue";
import PropertyReviewsList from "~/components/PropertyReviews/PropertyReviewsList.vue";
import dayjs from '~/utils/dayjs';
import {useSearchStore} from "~/store/search";
import { useRoute} from "vue-router";
import Dialog from "primevue/dialog";
import HotelRoomsSkeleton from "~/components/HotelRoomsSkeleton.vue";
defineEmits(['retry-rooms'])

const store = usePropertyStore()
const searchStore = useSearchStore();
const route = useRoute();
const isGoogleBot = computed(() => route.query.isgoogle === 'true')
let selectedRate = ref('')
let cancellationInfoPanel = ref({} as any)

const dateCancellationUntil = ''
let isModalVisible = ref(false as boolean)
let roomInfoDialogData = ref({} as any)
const soldOutRatePopup = ref(true)

const hasDates = computed(() => !!(searchStore.dates?.[0] && searchStore.dates?.[1]))

// Google price anchor — show notice if our price differs from what Google displayed
const googlePriceNotice = computed(() => {
  const gPrice = parseFloat(route.query?.['google-displayed-price'] as string)
  if (!gPrice || !store.rooms?.length) return null
  const cheapestRate = store.rooms[0]?.rates?.[0]
  if (!cheapestRate?.total_price?.converted?.amount) return null
  const ourTotal = parseFloat(cheapestRate.total_price.converted.amount)
  const diff = Math.abs(ourTotal - gPrice) / gPrice
  if (diff < 0.03) return null // within 3% — close enough
  if (ourTotal > gPrice) return `Price updated since your search`
  return null
})

// Google price match — check if a rate's total is within 3% of google-displayed-price
const googleDisplayedPrice = computed(() => parseFloat(route.query?.['google-displayed-price'] as string) || 0)
const googleDisplayedTax = computed(() => parseFloat(route.query?.['google-displayed-tax'] as string) || 0)

function isGoogleMatchedRate(rate: any) {
  if (!googleDisplayedPrice.value) return false
  const ourTotal = parseFloat(rate.total_price?.converted?.amount || 0)
  if (!ourTotal) return false
  const googleTotal = googleDisplayedPrice.value + googleDisplayedTax.value
  return Math.abs(ourTotal - googleTotal) / googleTotal < 0.03
}

// ---------------------------------------------------------------------------
// Google Hotel Ads price-accuracy Microdata.
//
// Google deprecated JSON-LD for hotel price-accuracy validation — its validator
// crawler reads Microdata, and the values it reads have to match what a guest
// sees on the page. We shipped price markup as JSON-LD only, so the validator
// had nothing to parse and fell back to guessing at the rendered DOM: on
// 2026-08-27 it reported ¥119,322 (base ¥88,999 + tax ¥30,323) for Hilton Tokyo
// Odaiba against a ¥81,715 feed price, a total that appears nowhere on the page.
// https://developers.google.com/hotels/hotel-prices/structured-data/hotel-price-structured-data
// ---------------------------------------------------------------------------

// Google's format is YYYY-MM-DD[THH:mm:ss] — a space separator instead of the
// "T" is not a valid DateTime and the stay window is dropped.
function isoStayDateTime(date: any, clockTime: any, fallbackClock: string) {
  if (!date) return ''
  const day = dayjs(String(date))
  if (!day.isValid()) return ''
  const clock = clockTime ? dayjs(String(clockTime), 'hh:mm A') : null
  return `${day.format('YYYY-MM-DD')}T${clock?.isValid() ? clock.format('HH:mm:ss') : fallbackClock}`
}

const microdataCheckin = computed(() => isoStayDateTime(
    route.query?.['date_start'] || route.query?.['checkin-date'],
    store.property?.check_in_time,
    '15:00:00',
))

const microdataCheckout = computed(() => isoStayDateTime(
    route.query?.['date_end'] || route.query?.['checkout-date'],
    store.property?.check_out_time,
    '11:00:00',
))

// Google treats address as optional, but once it is present every PostalAddress
// subfield is mandatory — half an address is worse than none.
const microdataAddress = computed(() => {
  const prop: any = store.property
  if (!prop?.id) return null
  const address = {
    streetAddress: prop.address || '',
    addressLocality: prop.city_name || prop.geo_place?.name || '',
    addressRegion: prop.region || prop.geo_place?.state_name || '',
    postalCode: prop.postal_code || '',
    addressCountry: prop.country_code || prop.geo_place?.country_code || '',
  }
  return Object.values(address).every(Boolean) ? address : null
})

function microdataAmount(price: any) {
  const amount = parseFloat(price?.converted?.amount ?? '')
  return Number.isFinite(amount) ? String(amount) : ''
}

// A breakdown that does not reconcile with the declared total is worse than no
// breakdown at all, so we only publish one when the components actually add up.
function microdataPriceComponents(rate: any) {
  const total = parseFloat(rate?.total_price?.converted?.amount ?? '')
  if (!Number.isFinite(total)) return []

  const fallbackCurrency = rate?.total_price?.converted?.currency || 'USD'
  const components: any[] = []

  const base = parseFloat(rate?.base_price?.converted?.amount ?? '')
  if (Number.isFinite(base)) {
    components.push({
      key: 'base',
      name: 'Base Rate',
      type: '',
      amount: String(base),
      currency: rate.base_price.converted.currency || fallbackCurrency,
    })
  }

  const addCharges = (charges: any[], defaultType: string) => {
    (charges || [])
        .filter((charge: any) => charge?.is_mandatory && parseFloat(charge?.price?.converted?.amount || 0) > 0)
        .forEach((charge: any, index: number) => {
          const type = charge.is_tax === false ? 'ResortFee' : (charge.is_tax ? 'GenericTax' : defaultType)
          components.push({
            key: `${defaultType}-${index}`,
            name: charge.name || type,
            type,
            amount: String(parseFloat(charge.price.converted.amount)),
            currency: charge.price.converted.currency || fallbackCurrency,
          })
        })
  }

  addCharges(rate?.taxes_and_fees, 'GenericTax')
  addCharges(rate?.surcharges, 'ResortFee')

  const sum = components.reduce((acc, component) => acc + parseFloat(component.amount), 0)
  return Math.abs(sum - total) <= 0.02 ? components : []
}

// Free cancellation banner
const hasFreeCancel = computed(() => {
  if (!store.rooms?.length) return false
  return store.rooms.some((room: any) =>
    room.rates?.some((rate: any) =>
      rate.is_refundable && rate.cancellation_policies?.some((p: any) =>
        new Date(p.dt_deadline) > new Date()
      )
    )
  )
})


//computed
const selectedRateInStore = computed(() => store.selectedRate)
const dates = computed(() => searchStore.dates.map((date:any) => date ? new Date(date) : null))
const currentSoldOut = computed(() => store.currentSoldOutRate)
const bookingPeriod = computed(() => searchStore.bookingPeriod)
const soldOutPopup = computed(() => searchStore.soldOutRooms)
const isDateChanged = computed(() => store.isDateChanged)
const loadingRooms = computed(() => searchStore.loadingRooms);

// Check if ALL rooms have no images (not just individual rooms)
const allRoomsLackImages = computed(() => {
  if (!store.rooms || store.rooms.length === 0) return false;
  return store.rooms.every((room: any) => !room.images || room.images.length === 0);
});

//methods
//open and close methods for sold out popup
function closeSoldOutRateOpenDates() {
  searchStore.setSoldOutRooms(false)
}


function  createCancellationDateInUserTimezone(rate: any) {
  const { cancellation_policies } = rate

  if (!cancellation_policies?.length) {
    return ''
  }

  let resultDateString = ''

  cancellation_policies?.forEach((policy: any)=> {
    const result = new Date().getTime() - new Date(policy?.dt_deadline).getTime()
    if (result < 0) {
      const date = dayjs(policy?.dt_deadline)
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

      resultDateString = dayjs(date)?.tz(userTimezone)?.format('hh:mm A on DD MMMM YYYY')
    }
  })

  return resultDateString
}

function currentDate() {
  const date = dayjs(new Date())
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return dayjs(date)?.tz(userTimezone)?.format('hh:mm A on DD MMMM YYYY')
}

function userTimezoneString() {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const userTimezoneFormatted = new Intl.DateTimeFormat('en', { timeZone: userTimezone, timeZoneName: 'short' })
    .formatToParts(new Date())
    .find(part => part.type === 'timeZoneName')?.value || userTimezone
  return `(${ userTimezoneFormatted })`
}

function closeInfoPopupAndOpenDates() {
  searchStore.setSoldOutRooms(false)
  store.setCurrentSoldOutRate('')
  soldOutRatePopup.value = false

  setTimeout(() => {
    document.querySelector<HTMLElement>('button.dates-selection')?.click()
  }, 100)
}
function setIndex(roomIndex: number, rateIndex: number) {
  return roomIndex*10 + rateIndex
}
function calculateOriginalRatePerNight(rate: any) {
  const nights = Math.abs(bookingPeriod.value || 0)

  if (!nights || !rate?.original_retail_price?.converted) {
    return ''
  }

  const amount = parseFloat(rate.original_retail_price.converted.amount || 0)
  const currency = rate.original_retail_price.converted.currency || 'USD'
  const occupancy = rate.sell_mode === 'per_person' ? (rate.occupancy || 1) : 1
  const perUnitAmount = amount / Math.max(1, nights) / occupancy

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Math.abs(perUnitAmount))
}

function calculateRatePerNight(rate: any) {
  const nights = Math.abs(bookingPeriod.value || 0)

  if (!nights || !rate?.base_price?.converted) {
    return ''
  }

  const amount = parseFloat(rate.base_price.converted.amount || 0)
  const currency = rate.base_price.converted.currency || 'USD'
  const occupancy = rate.sell_mode === 'per_person' ? (rate.occupancy || 1) : 1
  const perUnitAmount = amount / Math.max(1, nights) / occupancy

  return formatWithIntl(Math.abs(perUnitAmount), currency)
}

function formatCurrency(amount: number, currency: string) {
  // Shared formatter so every price line renders with the same (disambiguated) symbol as the
  // backend `.formatted` and the booking box — no more "$809.79" next to "SGD 269.93".
  return formatWithIntl(Math.abs(amount), currency)
}

function getMandatoryFees(rate: any) {
  return (rate.taxes_and_fees || []).filter((fee: any) => fee.is_mandatory && parseFloat(fee.price?.converted?.amount || 0) > 0)
}

function getTaxRecoveryFee(rate: any) {
  // Sum all mandatory taxes and fees into one line
  const fees = getMandatoryFees(rate)
  if (fees.length) {
    const currency = fees[0].price.converted.currency || 'USD'
    const total = fees.reduce((sum: number, fee: any) => sum + parseFloat(fee.price.converted.amount || 0), 0)
    if (total > 0.01) return formatCurrency(total, currency)
    return null
  }
  // Fall back to derived total
  return getTaxesAndFeesTotal(rate)
}

function getTaxesAndFeesTotal(rate: any) {
  if (rate.taxes_and_fees_total?.converted?.amount && parseFloat(rate.taxes_and_fees_total.converted.amount) > 0) {
    return formatPrice(rate.taxes_and_fees_total)
  }
  // Derive from total - base if both exist
  const total = parseFloat(rate.total_price?.converted?.amount || 0)
  const base = parseFloat(rate.base_price?.converted?.amount || 0)
  if (total > base && base > 0) {
    const diff = total - base
    // Subtract pay-at-property if present (it's not part of prepaid taxes)
    const payAtProp = parseFloat(rate.pay_at_property?.converted?.amount || 0)
    const surchargesTotal = (rate.surcharges || [])
      .filter((s: any) => !s.is_mandatory)
      .reduce((sum: number, s: any) => sum + parseFloat(s.price?.converted?.amount || 0), 0)
    const taxesPortion = diff - payAtProp - surchargesTotal
    if (taxesPortion > 0.01) {
      return formatCurrency(taxesPortion, rate.base_price.converted.currency || 'USD')
    }
  }
  return null
}

function getPayAtProperty(rate: any) {
  if (rate.pay_at_property?.converted?.amount && parseFloat(rate.pay_at_property.converted.amount) > 0) {
    return formatPrice(rate.pay_at_property)
  }
  // Must mirror ETL's total_surcharges, which sums MANDATORY surcharges only:
  // total_price = retail_price + mandatory surcharges. Filtering for
  // !is_mandatory hid exactly the fees that ARE in the total, so a $150
  // mandatory resort fee vanished from the breakdown while still inflating the
  // total — the card's own numbers stopped adding up (reported 2026-08-23).
  const surcharges = (rate.surcharges || []).filter((s: any) => s.is_mandatory && parseFloat(s.price?.converted?.amount || 0) > 0)
  if (surcharges.length) {
    const currency = surcharges[0].price.converted.currency || 'USD'
    const total = surcharges.reduce((sum: number, s: any) => sum + parseFloat(s.price.converted.amount || 0), 0)
    return formatCurrency(total, currency)
  }
  return null
}

function formatSavings(rate: any) {
  const original = parseFloat(rate.original_retail_price?.converted?.amount || 0)
  const current = parseFloat(rate.total_price?.converted?.amount || 0)
  if (original > current && current > 0) {
    return formatCurrency(original - current, rate.total_price.converted.currency || 'USD')
  }
  return null
}

function checkFreeCancellation(rate: any) {
  if (rate.is_refundable) {
    return rate.cancellation_policies?.some((policy: any) => {
      return (new Date().getTime() - new Date(policy?.dt_deadline).getTime()) < 0
    })
  }
  return false
}

function facilityList(room: any) {
  let result: any[] = []

  if (room.beds?.length) {
    const allBeds = room.beds?.reduce((accumulator: any, currentValue: any, index: any, array: any) => {
      accumulator += `${currentValue?.number} ${currentValue.title}`;
      if (index < array.length - 1) {
        accumulator += ', ';
      }
      return accumulator;
    }, '');

    result.push({
      iconType: 'image',
      icon: 'icon-bed',
      description: allBeds
    })
  }

  if (room?.square_footage) {
    result.push({
      iconType: 'icon',
      icon: 'icon-ruler',
      description: room?.square_footage
    })
  }

  if (room?.room_view) {
    result.push({
      iconType: 'icon',
      icon: 'icon-water-sun',
      description: room?.room_view
    })
  }

  const conditioning = room?.amenities.find((amenity: any) => Number(amenity.ota_code) === 2)
  const tv = room?.amenities.find((amenity: any) => Number(amenity.ota_code) === 251)
  const wiFi = room?.amenities.find((amenity: any) => Number(amenity.ota_code) === 123)
  const bath = room?.amenities.find((amenity: any) => Number(amenity.ota_code) === 13)

  if (conditioning) {
    result.push({
      iconType: 'icon',
      icon: 'icon-snowflake',
      description: conditioning.name
    })
  }

  if (wiFi) {
    result.push({
      iconType: 'icon',
      icon: 'icon-wifi',
      description: 'Wi-Fi'
    })
  }

  if (tv) {
    result.push({
      iconType: 'icon',
      icon: 'icon-tv',
      description: tv.name
    })
  }

  if (bath) {
    result.push({
      iconType: 'icon',
      icon: 'icon-bath',
      description: bath.name
    })
  }

  return result.splice(0, 3)
}

function openDatesSelector() {
  setTimeout(() => {
    document.querySelector<HTMLElement>('button.dates-selection')?.click()
  }, 100)
}

function findRoomIdFromRate(rateRef: string) {
  // const rrr = '362622GjHnA3NuTYWeiKaegePWL6e6TqLQKrb8mjZk8A6u43nFgTsf6Cnq711F8vdiSXoiLDRSY4747zx1mzN545iZfNHPW2UgnQUKuwFyzn4hAsv6hJpEDccuDNV7G63ywRP4vq1c1pcdmAMv8Dd38NSQ2SbnCPdGkTsD'
  // const currentRoom = store.rooms.find(room => room.rates.find(rate => rate?.ref === rrr))
  // console.log('findRoomIdFromRate', currentRoom)
  return 'room-' + rateRef
}


function showCancellationInfo(roomIndex: number, rateIndex: number) {
  cancellationInfoPanel.value[setIndex(roomIndex, rateIndex)] = true
}

function hideCancellationInfo(roomIndex: number, rateIndex: number) {
  cancellationInfoPanel.value[setIndex(roomIndex, rateIndex)] = false
}

function selectedRateChange(rate: any) {
  store.setSelectedRate(rate)
}

function openRoomInfo(room: any) {
  isModalVisible.value = true
  roomInfoDialogData.value = room
}

const config = useRuntimeConfig()
const configData = config.public
const brand = ($siteConfig as any).brand
const logo = ($siteConfig as any).logo
const siteHost = configData.baseURL

// Room JSON-LD is now emitted as part of Hotel.makesOffer in PropertyPage.vue
// (Google requires Hotel → makesOffer → Offer → containsPlace → HotelRoom)

//watchers
watch(selectedRateInStore, (newVal) => {
  if (!newVal?.ref) {
    selectedRate.value = ''
  }
})

watch(currentSoldOut, (newVal) => {
  if (newVal) {
    soldOutRatePopup.value = true
  }
})
</script>

<style lang="scss" scoped>
.property-details {
  $headerDesktopWidth: 167px;
  padding-top: 60px;

  @media screen and (max-width: 768px) {
    padding-top: 6px;
  }

  &--rooms {
    &.no-active {
      .room {
        opacity: .5;
        pointer-events: none;
      }
    }
  }

  h2 {
    width: $headerDesktopWidth;
    min-width: $headerDesktopWidth;

    //tablet
    @media screen and (max-width: 1024px) and (min-width: 768px){
      min-width: 132px;
    }

    &.pb-6 {
      @media screen and (max-width: 768px) {
        padding-bottom: 13px;
      }
    }
  }
  .room {
    padding-bottom: 40px;
    position: relative;

    @media screen and (max-width: 768px) {
      padding-bottom: 24px;
    }

    &:not(:last-child) {
      border-bottom: 1px solid var(--grey-border);
      margin-bottom: 32px;

      @media screen and (max-width: 768px) {
        margin-bottom: 20px;
      }
    }

    &__name {
      font-size: 24px;
      font-weight: 600;
      line-height: 133.333%;
      
      @media screen and (max-width: 768px) {
        font-size: 20px;
      }
    }
    
    &__summary {
      margin-top: 8px;
      line-height: 1.5;
    }
    
    .room-header {
      &--compact {
        margin-bottom: 16px;
        
        .room__name {
          font-size: 20px;
          font-weight: 600;
          color: var(--black);
          
          @media screen and (max-width: 768px) {
            font-size: 18px;
          }
        }
      }
    }

    .room-details {
      display: flex;
      gap: 24px;

      @media screen and (max-width: 768px) {
        gap: 16px;
      }

      .room-details__left {
        .room-images {
          :global(.gallery-preview) {
            width: 262px;
            height: 200px;
            overflow: hidden;
            border-radius: 10px;
          }
          :global(.gallery-preview .p-carousel-item img) {
            width: 262px;
            height: 200px;
            object-fit: cover;
          }
          .image-placeholder {
            width: 262px;
            height: 200px;
            border-radius: 10px;
          }
        }
        .room-amenities {
          display: flex;
          flex-wrap: wrap;
          column-gap: 16px;
          row-gap: 12px;
          width: 262px;
          padding-top: 13px;

          .icon {
            font-size: 18px;
            color: var(--dark-grey);
          }

          .room_amenity__icon {
            filter: brightness(0) saturate(100%) invert(36%) sepia(64%) saturate(0%) hue-rotate(246deg) brightness(83%) contrast(100%); //#595757
          }

          span {
            color: var(--dark-grey);
            font-weight: 500;
            font-size: 12px;
            display: flex;
            align-items: center;
            gap: 8px;
          }
        }
      }
      .room-details__right {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 12px;
        .room-rate {
          cursor: pointer;
          display: flex;
          gap: 0;
          border: 2px solid var(--grey-border);
          padding: 16px;
          border-radius: 10px;
          position: relative;

          @media screen and (max-width: 768px) {
            flex-direction: column;
            padding: 14px;
          }

          &:hover {
            border-color: var(--light-grey-border);

            .p-radiobutton .p-radiobutton-box {
              border-color: var(--light-grey-border);
            }
          }


          .room-rate_info {
            flex: 1;
            label {
              padding-top: 3px;
              padding-bottom: 4px;
              cursor: pointer;
            }
          }

          .room-rate_room_board {
            display: flex;
            gap: 8px;
            margin-bottom: 4px;

            span {
              font-weight: 400;
              font-size: 14px;
              line-height: 20px;
              letter-spacing: 0;
              color: var(--dark-grey-secondary);
            }
          }

          .cancellation-info {
            display: flex;
            gap: 8px;
            align-items: center;
            font-size: 13px;

            .refund-info {
              display: flex;
              align-items: center;
              gap: 8px;
            }

            &.fully-refundable {
              .label {
                color: var(--green);
              }

              .icon-info {
                color: var(--green);
              }
            }

            &.non-refundable {
              .label {
                color: var(--red);
              }

              .icon-info {
                color: var(--red);
              }
            }
          }
          .room-rate-description {
            display: flex;
            color: var(--dark-grey-secondary);
          }

          &__selected {
            border-color: var(--black);

            .room-rate-description {
              color: var(--dark-grey);
            }
          }

          &__radio {
            position: absolute;
            top: 16px;
            left: 16px;
            pointer-events: none;

            @media screen and (max-width: 768px) {
              top: 14px;
              left: 14px;
            }

            :deep(.p-radiobutton) {
              width: 20px;
              height: 20px;
            }

            :deep(.p-radiobutton-box) {
              width: 0;
              height: 0;
              opacity: 0;
              margin: 0;
              border-width: 0;
            }
          }
        }
      }

      &__price-col {
        width: 180px;
        min-width: 180px;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 6px;

        @media screen and (max-width: 768px) {
          width: 100%;
          min-width: 0;
          align-items: flex-start;
          border-top: 1px solid var(--grey-border);
          padding-top: 10px;
          margin-top: 4px;

          .price-header {
            text-align: left;
          }
          .price-lines {
            border-top: none;
            padding-top: 0;
          }
        }
      }
      
      // Compact amenities layout when all rooms lack images
      &__amenities-compact {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 12px 0 20px 0;
        
        @media screen and (max-width: 768px) {
          flex-direction: column;
          align-items: flex-start;
          gap: 12px;
        }
        
        .amenities-list {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 20px;
          
          @media screen and (max-width: 768px) {
            gap: 16px;
          }
        }
        
        .amenity-item {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 500;
          color: var(--dark-grey);
          white-space: nowrap;
          
          .icon {
            font-size: 14px;
            color: var(--dark-grey);
            flex-shrink: 0;
          }
          
          .room_amenity__icon {
            filter: brightness(0) saturate(100%) invert(36%) sepia(64%) saturate(0%) hue-rotate(246deg) brightness(83%) contrast(100%);
            flex-shrink: 0;
          }
          
          .amenity-text {
            line-height: 1.3;
          }
        }
        
        .btn--more-compact {
          font-size: 13px;
          font-weight: 500;
          padding: 0;
          white-space: nowrap;
          flex-shrink: 0;
          
          .icon {
            font-size: 14px;
            margin-left: 4px;
          }
        }
      }
    }
    
    // Adjust layout when all rooms have no images
    &.room--no-images {
      padding-bottom: 32px;
      
      .room-details {
        display: block;
      }
      
      .room-details__right {
        .room-rate {
          padding: 12px 16px;
          gap: 12px;

          @media screen and (max-width: 768px) {
            flex-direction: column;
            padding: 14px;
            gap: 0;
          }

          .room-rate_info {
            label {
              padding-top: 0;
              padding-bottom: 2px;

              .text-body-1 {
                font-size: 15px;
                font-weight: 500;
              }
            }
          }

          &__radio {
            position: relative;
            top: 0;
            left: 0;
            pointer-events: all;
            margin-right: 12px;

            @media screen and (max-width: 768px) {
              position: absolute;
              top: 14px;
              left: 14px;
              margin-right: 0;
            }
          }
        }
      }
    }
  }
  .block-split {
    display: flex;
    gap: 24px;
    align-items: flex-start;
    & > h2 {
      width: $headerDesktopWidth;
      min-width: $headerDesktopWidth;
    }
    .container {
      flex: 1;
    }
  }
  .rooms-list-placeholder {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;


    .placeholder-block {
      background-color: var(--light-grey);
      border-radius: 10px;
    }
    .rooms-list-placeholder__line {
      display: flex;
      padding: 14px 10px;
      align-items: center;
      .icon {
        font-size: 28px;
      }
    }
    .rooms-list-placeholder__list {
      display: flex;
      gap: 24px;
      width: 100%;
      flex-wrap: wrap;

      @media screen and (max-width: 768px) {
        justify-content: space-between;
        gap: 8px;
      }


      .placeholder-block {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        align-items: center;
        gap: 20px;
        min-width: calc((100% / 3) - 16px);
        height: 132px;
        font-size: 14px;
        font-weight: 500;
        padding-bottom: 12px;
        border: 2px solid var(--grey);

        //tablet
        @media screen and (max-width: 1024px) and (min-width: 769px) {
          height: 117px;
        }

        //vertical tablet and mobile
        @media screen and (max-width: 768px) {
          height: 104px;
          padding-bottom: 10px;
          min-width: calc((100% / 3) - 10px);
        }
      }
    }
  }
}

.room-dialog-header-wrapper {
  display: flex;

  @media screen and (max-width: 768px) {
    align-items: center;
    gap: 24px;
  }
}

.room-info-dialog-header {
  line-height: 1.1;

  @media screen and (max-width: 768px) {
    font-size: 20px;
    font-weight: 600;
    line-height: 24px;
  }
}

.btn--more {
  font-size: 14px;
  cursor: pointer;

  .icon {
    font-size: 16px;
    margin-left: 8px;
    position: relative;
    top: 2px;
  }
}

.refund-info {
  .info-icon-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    .icon-information-solid {
      display: none;
    }
    &:hover {
      cursor: pointer;
      .icon-information-solid {
        display: inline;
        color: var(--black);
      }
      .icon-info {
        display: none;
      }
    }
  }
}

:global(.room-modal-container) {
  width: 100%;
  max-width: 708px;
  height: 100%;
  max-height: 100%;
  padding: 24px;
  overflow-y: auto;
  background: var(--light-grey);
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);

  @media screen and (max-width: 768px) {
    padding: 16px;
  }
}

@media screen and (max-width: 1024px) {
  .property-details .room .room-details {
    display: block;
    .room-details__left {
      width: 100%;
      padding-bottom: 20px;
      :global(.gallery-preview) {
        width: 100%;
        height: 230px;
      }
      :global(.gallery-preview .p-carousel-item img) {
        width: 100%;
        height: 230px;
      }
      .room-images .image-placeholder {
        width: 100%;
        height: 230px;
      }
      .room-amenities {
        width: 100%;
      }
    }
  }
}

.sold-out-rooms {
  position: relative;
  width: 100%;
  min-height: 372px;
}

.google-match-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #1a73e8;
  background: #e8f0fe;
  padding: 2px 8px;
  border-radius: 4px;
  margin-top: 4px;
  width: fit-content;
}

.free-cancel-banner {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f0faf0;
  color: #1a7a1a;
  font-size: 13px;
  font-weight: 500;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 16px;
}

.rooms-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  font-size: 14px;
  color: var(--dark-grey);

  button {
    margin-left: 4px;
  }
}

.price-header {
  text-align: right;

  &__main {
    line-height: 1.2;
  }

  &__amount {
    font-size: 18px;
    font-weight: 700;
    color: var(--black);
    letter-spacing: -0.02em;
  }

  &__strike {
    text-decoration: line-through;
    color: var(--dark-grey);
    font-size: 13px;
    font-weight: 400;
    margin-right: 4px;
  }

  &__badge {
    display: inline-block;
    background-color: var(--green);
    color: white;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 5px;
    border-radius: 3px;
    margin-left: 4px;
    vertical-align: super;
  }

  &__sub {
    font-size: 11px;
    color: var(--dark-grey);
    margin-top: 1px;
  }

  &__savings {
    font-size: 11px;
    font-weight: 600;
    color: var(--green);
    margin-top: 2px;
  }

  &__promo {
    font-size: 11px;
    font-weight: 600;
    color: var(--green);
  }

  &__google-note {
    font-size: 10px;
    color: var(--dark-grey);
    font-style: italic;
    margin-top: 2px;
  }
}

.price-lines {
  width: 100%;
  font-variant-numeric: tabular-nums;
  border-top: 1px solid var(--light-grey, #eee);
  padding-top: 4px;

  &__row {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    font-size: 11px;
    color: var(--dark-grey);
    line-height: 1.8;

    &--property {
      color: var(--blue);
    }
  }

  &__note {
    display: block;
    font-size: 10px;
    line-height: 1.4;
    color: var(--dark-grey);
  }

  &__total {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    font-size: 13px;
    font-weight: 600;
    color: var(--black);
    border-top: 1px solid var(--light-grey, #ddd);
    margin-top: 2px;
    padding-top: 3px;
    line-height: 1.8;
  }

  &__incl {
    font-size: 10px;
    color: var(--dark-grey);
    text-align: right;

    @media screen and (max-width: 768px) {
      text-align: left;
    }
  }
}
</style>

<style lang="scss">
.room-details__right {
  .room-rate {
    &:hover {
      .p-radiobutton .p-radiobutton-box {
        border-color: var(--blue);
      }
    }
  }
}
</style>
